import { NUMERIC_GROUP_SEPARATOR, NUMERIC_INPUT_MAX_DIGITS } from '@/lib/constants/numeric-input';

const NON_DIGITS = /\D/g;
const LEADING_ZEROS = /^0+(?=\d)/;
const DIGIT = /\d/;
const THOUSANDS_BOUNDARY = /\B(?=(\d{3})+(?!\d))/g;

/** Every non-digit dropped; no cap, no zero handling (used to read a stored value). */
export function stripToDigits(raw: string): string {
  return raw.replace(NON_DIGITS, '');
}

/** `5050505` -> `5,050,505`. Expects a plain digit string. */
export function groupDigits(digits: string): string {
  return digits.replace(THOUSANDS_BOUNDARY, NUMERIC_GROUP_SEPARATOR);
}

const WHOLE_DIGITS = /^\d+$/;
const DECIMAL_DIGITS = /^\d+\.\d*$/;

/**
 * The plain whole-number digits for a stored or restored value, for display. A saved decimal
 * (`1500.25`, `'1500.25'`) is ROUNDED to whole units (`'1500'`), never read with its dot stripped
 * (which would show 150,025); anything else non-numeric is reduced to its digits. Blank, negative
 * and non-finite values show nothing. Pure.
 */
export function toWholeDigits(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return '';
    // BigInt keeps a huge value as digits (String(1e21) would be "1e+21").
    return BigInt(Math.round(value)).toString();
  }
  const text = value.trim();
  if (WHOLE_DIGITS.test(text)) return text;
  if (DECIMAL_DIGITS.test(text)) return toWholeDigits(Number(text));
  return stripToDigits(text);
}

export interface SanitizedNumericInput {
  /** The plain digit string the form holds. */
  digits: string;
  /** How many of `digits` sit before the caret. */
  caretDigits: number;
}

/**
 * Turns whatever the input now contains (typed, pasted, or half-edited grouped
 * text) into a plain digit string: non-digits dropped, leading zeros removed (a
 * lone `0` stays), and over-long input cut back to `maxDigits` by ignoring the digits
 * just entered at the caret (so typing into a full number changes nothing, and pasting at
 * the end keeps the leading digits). `caretDigits` tracks the caret as a count of digits,
 * which survives regrouping - the caller maps it back to a position in the grouped text
 * with `caretIndexForDigits`.
 */
export function sanitizeNumericInput(
  raw: string,
  caret: number,
  maxDigits: number = NUMERIC_INPUT_MAX_DIGITS,
): SanitizedNumericInput {
  const rawDigits = stripToDigits(raw);
  const withoutZeros = rawDigits.replace(LEADING_ZEROS, '');
  const removedZeros = rawDigits.length - withoutZeros.length;
  const digitsBeforeCaret = stripToDigits(raw.slice(0, caret)).length;
  const caretDigits = Math.min(Math.max(digitsBeforeCaret - removedZeros, 0), withoutZeros.length);

  const excess = withoutZeros.length - maxDigits;
  if (excess <= 0) return { digits: withoutZeros, caretDigits };

  // Drop the excess digits ending at the caret (the ones just typed or pasted); if the caret is
  // too close to the start to take them all, drop the remainder from the end.
  const fromCaret = Math.min(excess, caretDigits);
  const kept = withoutZeros.slice(0, caretDigits - fromCaret) + withoutZeros.slice(caretDigits);
  return { digits: kept.slice(0, maxDigits), caretDigits: caretDigits - fromCaret };
}

/** Position in `shown` immediately after its `digitCount`-th digit (0 for none). */
export function caretIndexForDigits(shown: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < shown.length; index += 1) {
    if (DIGIT.test(shown[index])) seen += 1;
    if (seen === digitCount) return index + 1;
  }
  return shown.length;
}
